import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/api/client";
import { useAuth } from "@/context/AuthContext";
import { Plus, X, ExternalLink, Pencil, Archive, CheckCircle2, RotateCcw } from "lucide-react";
import { Link } from "react-router-dom";

const inr = (v) => `₹ ${Number(v || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;

const STATUS_STYLE = {
    open: "bg-green-50 border-green-200 text-green-700",
    in_comparison: "bg-amber-50 border-amber-200 text-amber-800",
    awarded: "bg-blue-50 border-blue-200 text-blue-700",
    closed: "bg-stone-100 border-stone-200 text-stone-600",
    archived: "bg-stone-100 border-stone-200 text-stone-500",
};

const STATUS_LABEL = {
    open: "Open",
    in_comparison: "In comparison",
    awarded: "Awarded",
    closed: "Closed",
    archived: "Archived",
};

const EMPTY_FORM = {
    name: "", code: "", client_id: "", budget: 0, notes: "",
    visibility_user_ids: [], vendor_assignments: [], status: "open",
};

export default function Projects() {
    const { isAdmin } = useAuth();
    const [items, setItems] = useState([]);
    const [clients, setClients] = useState([]);
    const [vendors, setVendors] = useState([]);
    const [users, setUsers] = useState([]);
    const [showArchived, setShowArchived] = useState(false);
    const [modal, setModal] = useState(null); // { mode: 'create'|'edit', form }
    const [err, setErr] = useState("");

    const load = async () => {
        const [p, c, v, u] = await Promise.all([
            api.get(`/projects${showArchived ? "?include_archived=true" : ""}`),
            api.get("/clients"), api.get("/vendors"), api.get("/users"),
        ]);
        setItems(p.data); setClients(c.data); setVendors(v.data); setUsers(u.data);
    };

    useEffect(() => { load(); }, [showArchived]); // eslint-disable-line

    const openCreate = () => setModal({ mode: "create", form: { ...EMPTY_FORM } });
    const openEdit = (p) => setModal({
        mode: "edit",
        form: {
            id: p.id,
            name: p.name || "",
            code: p.code || "",
            client_id: p.client_id || "",
            budget: p.budget || 0,
            notes: p.notes || "",
            visibility_user_ids: p.visibility_user_ids || [],
            vendor_assignments: (p.vendor_assignments || []).map(va => ({ user_id: va.user_id, vendor_id: va.vendor_id })),
            status: p.status || "open",
        },
    });

    const submit = async (e) => {
        e.preventDefault();
        setErr("");
        const form = modal.form;
        const payload = {
            name: form.name, code: form.code, client_id: form.client_id,
            budget: Number(form.budget) || 0, notes: form.notes,
            visibility_user_ids: form.visibility_user_ids,
            vendor_assignments: form.vendor_assignments,
            status: form.status,
        };
        try {
            if (modal.mode === "edit") await api.put(`/projects/${form.id}`, payload);
            else await api.post("/projects", payload);
            setModal(null);
            load();
        } catch (e) {
            setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        }
    };

    const changeStatus = async (p, next) => {
        try { await api.post(`/projects/${p.id}/status?status=${next}`); load(); }
        catch (e) { alert(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    };

    const setForm = (patch) => setModal({ ...modal, form: { ...modal.form, ...patch } });

    return (
        <div data-testid="projects-page" className="space-y-6">
            <div className="flex items-center justify-between">
                <div>
                    <div className="text-[11px] uppercase tracking-wider text-stone-500 mb-1">Master data</div>
                    <h1 className="text-3xl font-heading font-semibold text-stone-900 tracking-tight">Projects</h1>
                </div>
                <div className="flex items-center gap-3">
                    {isAdmin() && (
                        <label className="text-xs text-stone-600 flex items-center gap-1.5">
                            <input type="checkbox" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} /> Show archived
                        </label>
                    )}
                    {isAdmin() && (
                        <button data-testid="new-project-btn" onClick={openCreate}
                                className="bg-amber-500 hover:bg-amber-600 text-white rounded-md px-4 py-2 text-sm font-medium flex items-center gap-2">
                            <Plus size={15} /> New project
                        </button>
                    )}
                </div>
            </div>

            <div className="bg-white border border-stone-200 rounded-xl overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="bg-stone-50 text-[11px] uppercase tracking-wider text-stone-500">
                                <th className="text-left py-3 px-4">Name</th>
                                <th className="text-left py-3 px-4">Code</th>
                                <th className="text-left py-3 px-4">Client</th>
                                <th className="text-right py-3 px-4">Budget</th>
                                <th className="text-left py-3 px-4">BOM</th>
                                <th className="text-left py-3 px-4">Status</th>
                                <th className="text-right py-3 px-4">Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {items.map((p) => (
                                <tr key={p.id} className="border-t border-stone-100 hover:bg-stone-50">
                                    <td className="py-3 px-4 font-medium text-stone-900">{p.name}</td>
                                    <td className="py-3 px-4 text-stone-600 font-mono text-xs">{p.code || "—"}</td>
                                    <td className="py-3 px-4 text-stone-700">{p.client_name || "—"}</td>
                                    <td className="py-3 px-4 text-right">{p.budget ? inr(p.budget) : <span className="text-stone-400">—</span>}</td>
                                    <td className="py-3 px-4">
                                        <span className={"text-xs px-2 py-0.5 rounded-md border " + ((p.bom || []).length ? "bg-green-50 border-green-200 text-green-700" : "bg-stone-100 border-stone-200 text-stone-500")}>
                                            {(p.bom || []).length ? `${p.bom.length} items` : "Not uploaded"}
                                        </span>
                                    </td>
                                    <td className="py-3 px-4">
                                        <span className={"text-xs px-2 py-0.5 rounded-md border " + (STATUS_STYLE[p.status] || STATUS_STYLE.open)}>
                                            {STATUS_LABEL[p.status] || "Open"}
                                        </span>
                                    </td>
                                    <td className="py-3 px-4 text-right space-x-3 whitespace-nowrap">
                                        <Link data-testid={`open-project-${p.id}`} to={`/projects/${p.id}`} className="text-amber-700 hover:text-amber-900 text-sm inline-flex items-center gap-1">
                                            Open <ExternalLink size={12} />
                                        </Link>
                                        {isAdmin() && (
                                            <>
                                                <button data-testid={`edit-project-${p.id}`} onClick={() => openEdit(p)} className="text-stone-700 hover:text-amber-700 text-xs inline-flex items-center gap-1">
                                                    <Pencil size={12} /> Edit
                                                </button>
                                                {p.status !== "closed" && p.status !== "archived" && (
                                                    <button data-testid={`close-project-${p.id}`} onClick={() => changeStatus(p, "closed")} className="text-stone-700 hover:text-blue-700 text-xs inline-flex items-center gap-1">
                                                        <CheckCircle2 size={12} /> Close
                                                    </button>
                                                )}
                                                {p.status !== "archived" && (
                                                    <button data-testid={`archive-project-${p.id}`} onClick={() => changeStatus(p, "archived")} className="text-stone-700 hover:text-red-700 text-xs inline-flex items-center gap-1">
                                                        <Archive size={12} /> Archive
                                                    </button>
                                                )}
                                                {(p.status === "archived" || p.status === "closed") && (
                                                    <button data-testid={`reopen-project-${p.id}`} onClick={() => changeStatus(p, "open")} className="text-green-700 hover:text-green-900 text-xs inline-flex items-center gap-1">
                                                        <RotateCcw size={12} /> Reopen
                                                    </button>
                                                )}
                                            </>
                                        )}
                                    </td>
                                </tr>
                            ))}
                            {items.length === 0 && (
                                <tr><td colSpan={7} className="py-10 text-center text-stone-500 text-sm">No projects {showArchived ? "" : "(archived hidden)"}.</td></tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {modal && (
                <ProjectFormModal
                    mode={modal.mode}
                    form={modal.form}
                    setForm={setForm}
                    clients={clients}
                    vendors={vendors}
                    users={users}
                    err={err}
                    onClose={() => { setModal(null); setErr(""); }}
                    onSubmit={submit}
                />
            )}
        </div>
    );
}

function ProjectFormModal({ mode, form, setForm, clients, vendors, users, err, onClose, onSubmit }) {
    const isEdit = mode === "edit";
    const activeVendors = vendors.filter(v => v.status !== "blacklisted");
    const nonAdminUsers = users.filter(u => u.role !== "administrator");

    return (
        <div className="fixed inset-0 z-40 bg-stone-900/40 flex items-center justify-center p-4">
            <div className="bg-white border border-stone-200 rounded-xl max-w-2xl w-full max-h-[92vh] overflow-y-auto">
                <div className="px-6 py-4 border-b border-stone-200 flex items-center justify-between sticky top-0 bg-white">
                    <div className="font-heading font-semibold text-stone-900">{isEdit ? "Edit project" : "New project"}</div>
                    <button data-testid="close-project-modal" onClick={onClose} className="text-stone-500 hover:text-stone-900"><X size={18} /></button>
                </div>
                <form onSubmit={onSubmit} className="p-6 space-y-4">
                    {err && <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2">{err}</div>}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="overline block mb-1">Name *</label>
                            <input data-testid="proj-name" required value={form.name} onChange={(e) => setForm({ name: e.target.value })} className="w-full border border-stone-300 rounded-md text-sm py-2 px-3 focus:outline-none focus:border-amber-500 focus:ring-1 focus:ring-amber-500" />
                        </div>
                        <div>
                            <label className="overline block mb-1">Code</label>
                            <input data-testid="proj-code" value={form.code} onChange={(e) => setForm({ code: e.target.value })} className="w-full border border-stone-300 rounded-md text-sm py-2 px-3" />
                        </div>
                        <div>
                            <label className="overline block mb-1">Client</label>
                            <select data-testid="proj-client" value={form.client_id} onChange={(e) => setForm({ client_id: e.target.value })} className="w-full border border-stone-300 rounded-md text-sm py-2 px-3">
                                <option value="">—</option>
                                {clients.map(c => <option key={c.id} value={c.id}>{c.company || c.name}</option>)}
                            </select>
                        </div>
                        <div>
                            <label className="overline block mb-1">Budget (₹)</label>
                            <input data-testid="proj-budget" type="number" min="0" step="1" value={form.budget} onChange={(e) => setForm({ budget: e.target.value })} className="w-full border border-stone-300 rounded-md text-sm py-2 px-3" />
                        </div>
                        {isEdit && (
                            <div className="md:col-span-2">
                                <label className="overline block mb-1">Status</label>
                                <select data-testid="proj-status" value={form.status} onChange={(e) => setForm({ status: e.target.value })} className="w-full md:w-64 border border-stone-300 rounded-md text-sm py-2 px-3">
                                    <option value="open">Open</option>
                                    <option value="in_comparison">In comparison</option>
                                    <option value="awarded">Awarded</option>
                                    <option value="closed">Closed</option>
                                    <option value="archived">Archived</option>
                                </select>
                            </div>
                        )}
                    </div>

                    <div>
                        <label className="overline block mb-2">Visibility — users who can see this project</label>
                        {nonAdminUsers.length === 0 ? (
                            <div className="text-xs text-stone-500 border border-stone-200 rounded-md p-3">No non-admin users yet. Create users first in <span className="font-medium">Users</span>.</div>
                        ) : (
                            <div className="border border-stone-200 rounded-md p-3 max-h-40 overflow-y-auto space-y-1">
                                {nonAdminUsers.map(u => (
                                    <label key={u.id} className="flex items-center gap-2 text-sm">
                                        <input type="checkbox"
                                               checked={form.visibility_user_ids.includes(u.id)}
                                               onChange={(e) => {
                                                   const next = e.target.checked
                                                       ? [...form.visibility_user_ids, u.id]
                                                       : form.visibility_user_ids.filter(x => x !== u.id);
                                                   setForm({ visibility_user_ids: next });
                                               }} />
                                        <span className="text-stone-800">{u.name}</span>
                                        <span className="text-xs text-stone-500">({u.department} · {u.role?.replace("_", " ")})</span>
                                    </label>
                                ))}
                            </div>
                        )}
                    </div>

                    <div>
                        <label className="overline block mb-2">Vendor assignments — per user, for this project</label>
                        {isEdit && (form.vendor_assignments || []).length === 0 && (
                            <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-3 py-2 mb-2">
                                No vendors assigned. Purchase members you added to visibility will not be able to submit a quotation until you assign each of them a vendor here.
                            </div>
                        )}
                        <div className="space-y-2">
                            {(form.vendor_assignments || []).map((va, i) => (
                                <div key={i} className="grid grid-cols-1 md:grid-cols-[1fr_1fr_auto] gap-2 items-center">
                                    <select data-testid={`va-user-${i}`} value={va.user_id} onChange={(e) => {
                                        const arr = [...form.vendor_assignments]; arr[i] = { ...arr[i], user_id: e.target.value }; setForm({ vendor_assignments: arr });
                                    }} className="border border-stone-300 rounded-md text-sm py-2 px-3">
                                        <option value="">Select user</option>
                                        {users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
                                    </select>
                                    <select data-testid={`va-vendor-${i}`} value={va.vendor_id} onChange={(e) => {
                                        const arr = [...form.vendor_assignments]; arr[i] = { ...arr[i], vendor_id: e.target.value }; setForm({ vendor_assignments: arr });
                                    }} className="border border-stone-300 rounded-md text-sm py-2 px-3">
                                        <option value="">Select vendor</option>
                                        {activeVendors.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
                                    </select>
                                    <button type="button" onClick={() => setForm({ vendor_assignments: form.vendor_assignments.filter((_, j) => j !== i) })} className="text-stone-500 hover:text-red-700 text-sm">Remove</button>
                                </div>
                            ))}
                            <button type="button" data-testid="add-assignment"
                                    onClick={() => setForm({ vendor_assignments: [...(form.vendor_assignments || []), { user_id: "", vendor_id: "" }] })}
                                    className="text-sm text-amber-700 hover:text-amber-900 flex items-center gap-1"><Plus size={13} /> Add assignment</button>
                        </div>
                    </div>

                    <div>
                        <label className="overline block mb-1">Notes</label>
                        <textarea value={form.notes} onChange={(e) => setForm({ notes: e.target.value })} className="w-full border border-stone-300 rounded-md text-sm py-2 px-3" rows={3} />
                    </div>

                    <div className="flex justify-end gap-3 pt-2 border-t border-stone-200">
                        <button type="button" onClick={onClose} className="text-sm text-stone-600 hover:text-stone-900 px-4 py-2 rounded-md">Cancel</button>
                        <button data-testid="save-project" type="submit" className="bg-amber-500 hover:bg-amber-600 text-white rounded-md px-4 py-2 text-sm font-medium">
                            {isEdit ? "Save changes" : "Create project"}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}
